const libPictView = require('pict-view');
const libSampleLocationCartSession = require('../samples/SampleLocationCart-Session.js');

// The page's own cart instance: separate from the panels' Scan Samples and the Sample and Location pages' carts.
const CART_NAME = 'ScanSamplesCart';
const CART_HOST_ID = 'ScanSamplesPage-Cart';
// Its device settings ("Save each scan", the remembered location) are kept apart from the panels' cart's.
const SETTINGS_PREFIX = 'ScanSamplesPage-';

/**
 * The device's storage under this page's own prefix, so the page and the panels' Scan Samples remember their settings
 * separately. Storage the browser refuses (a private window) just keeps nothing.
 * @return {{ getItem: (pKey: string) => string|null, setItem: (pKey: string, pValue: string) => void, removeItem: (pKey: string) => void }}
 */
function deviceStorage()
{
	const fStorage = () =>
	{
		try
		{
			return ((typeof window !== 'undefined') && window.localStorage) ? window.localStorage : null;
		}
		catch (pError)
		{
			return null;
		}
	};
	return {
		getItem: (pKey) =>
		{
			const tmpStorage = fStorage();
			return tmpStorage ? tmpStorage.getItem(`${SETTINGS_PREFIX}${pKey}`) : null;
		},
		setItem: (pKey, pValue) =>
		{
			const tmpStorage = fStorage();
			if (tmpStorage)
			{
				tmpStorage.setItem(`${SETTINGS_PREFIX}${pKey}`, pValue);
			}
		},
		removeItem: (pKey) =>
		{
			const tmpStorage = fStorage();
			if (tmpStorage)
			{
				tmpStorage.removeItem(`${SETTINGS_PREFIX}${pKey}`);
			}
		},
	};
}

/**
 * Scan Samples (#/ScanSamples): the panels' Scan Samples cart as a page of its own; the LIMS micro app's top nav
 * Scanning Cart links here (web/lims/html/index.html). It
 * works as the panel does (pick or scan a location, collect samples and Move all or save each scan right away, Undo,
 * remembered settings, the camera, label printing, the action cards), with its own cart: nothing it holds or remembers
 * is shared with the panel. Leaving the page closes the cart (it stops taking scans and its camera stops).
 */
class ScanSamplesPage extends libPictView
{
	/**
	 * @param {any} pFable
	 * @param {any} pOptions
	 * @param {string} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		/** @type {any} */
		this.options;
	}

	/** @return {any} the page's cart session, made on first use */
	get session()
	{
		if (!this._session)
		{
			this._session = new libSampleLocationCartSession(
				{
					Pict: () => this.pict,
					CartName: CART_NAME,
					HostID: CART_HOST_ID,
					OwnHash: () => '#/ScanSamples',
					Storage: deviceStorage(),
					RememberSettings: true,
					Notify: (pToast) => this.toast(pToast),
					Toast: (pMessage, pType) => this.toast({ message: pMessage, type: (pType === 'error') ? 'is-danger' : ((pType === 'success') ? 'is-success' : 'is-info') }),
					PrintLabel: (pSample) => this.printSampleLabel(pSample),
				});
		}
		return this._session;
	}

	/** @return {any} the page's cart provider, once the page has drawn */
	get cart()
	{
		return this.pict.providers[CART_NAME];
	}

	/**
	 * @param {{ message: string, type: string }} pToast
	 */
	toast(pToast)
	{
		const tmpPanels = this.pict.views.HeadlightPanels;
		if (tmpPanels && (typeof tmpPanels.toast === 'function'))
		{
			tmpPanels.toast(pToast);
			return;
		}
		this.pict.log.info(`ScanSamplesPage: ${pToast.message}`);
	}

	/**
	 * Print a collected sample's label, as the panel does: the customer's label layout, with the sample already in hand.
	 * @param {Record<string, any>} pSample
	 * @return {Promise<void>}
	 */
	async printSampleLabel(pSample)
	{
		const tmpQRCode = this.pict.providers.QRCodeProvider;
		if (!tmpQRCode || !pSample || !pSample.QRCode)
		{
			return;
		}
		await tmpQRCode.ensurePrintableManifests();
		tmpQRCode.printDecoratedEntity('Sample', pSample.QRCode, undefined, undefined, pSample);
	}

	/**
	 * The page was drawn: open the cart in it (or redraw an open one).
	 * @param {any} pRenderable
	 * @return {any}
	 */
	onAfterRender(pRenderable)
	{
		this.session.mount();
		return super.onAfterRender(pRenderable);
	}
}

module.exports = ScanSamplesPage;
module.exports.CART_NAME = CART_NAME;
module.exports.SETTINGS_PREFIX = SETTINGS_PREFIX;
module.exports.default_configuration = (
	{
		ViewIdentifier: 'ScanSamplesPage',
		DefaultRenderable: 'ScanSamplesPage',
		DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',

		AutoRender: false,

		CSS: /*css*/`
			.ssp-cart { max-width: 44rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; overflow: hidden; }
			.ssp-cart .hl-scan {
				--hl-scan-background: var(--theme-color-background-panel, var(--theme-color-background-primary, #fff));
				--hl-scan-text: var(--theme-color-text-secondary, #45505f);
				--hl-scan-text-strong: var(--theme-color-text-primary, #1f2733);
				--hl-scan-text-weak: var(--theme-color-text-muted, #8a94a3);
				--hl-scan-border: var(--theme-color-border-light, #e8ebf0);
				--hl-scan-border-weak: var(--theme-color-border-light, #eef1f5);
				--hl-scan-head-background: var(--theme-color-background-tertiary, #f4f6f9);
				--hl-scan-input-background: var(--theme-color-background-primary, #fff);
				--hl-scan-input-text: var(--theme-color-text-primary, #1f2733);
				--hl-scan-input-border: var(--theme-color-border-default, #d7dce3);
			}
		`,

		Templates: [
			{
				Hash: 'ScanSamplesPage-Wrapper',
				Template: /*html*/`
				<section id="ScanSamplesPage" class="form p-5">
					<h2 class="title is-4 mb-3"><span class="icon mr-2"><i class="fas fa-qrcode"></i></span>Scan Samples</h2>
					<div id="${ CART_HOST_ID }" class="ssp-cart"></div>
				</section>`,
			},
		],
		Renderables: [
			{
				RenderableHash: 'ScanSamplesPage',
				TemplateHash: 'ScanSamplesPage-Wrapper',
				RenderMethod: 'replace',
			} ],
	});
